import * as metaPixel from "@/lib/meta-pixel";

let memoryAttribution: BrokerLeadAttribution | null = null;

const STORAGE_KEY = "venturu_broker_lead_attribution";
const VIEW_CONTENT_STORAGE_KEY = "venturu_broker_lead_view_content";
const FORM_OPEN_STORAGE_KEY = "venturu_broker_lead_form_open";

export function buildMetaFbc(fbclid: string, timestamp = Date.now()) {
  return `fb.1.${timestamp}.${fbclid}`;
}

export type BrokerLeadAttribution = {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent?: string;
  siteSourceName?: "fb" | "ig";
  fbclid?: string;
  eventSourceUrl?: string;
};

export type BrokerLeadAttributionEntry = Pick<
  BrokerLeadAttribution,
  "utmSource" | "utmMedium" | "utmCampaign" | "utmContent" | "siteSourceName"
>;

function firstSearchParam(
  value: string | string[] | undefined,
): string | undefined {
  const raw = Array.isArray(value) ? value[0] : value;
  const trimmed = raw?.trim();
  return trimmed ? trimmed : undefined;
}

function metaSiteSource(
  value: string | string[] | undefined,
): "fb" | "ig" | undefined {
  const normalized = firstSearchParam(value)?.toLowerCase();
  if (normalized === "fb" || normalized === "ig") return normalized;
  return undefined;
}

/** Same Meta paid-click rule as `captureBrokerLeadAttributionFromUrl`, from the request URL. */
export function leadGenEntryFromSearchParams(
  search: Record<string, string | string[] | undefined>,
): BrokerLeadAttributionEntry | undefined {
  const utmSource = firstSearchParam(search.utm_source);
  const utmMedium = firstSearchParam(search.utm_medium);
  const utmCampaign = firstSearchParam(search.utm_campaign);
  if (utmSource !== "meta" || utmMedium !== "paid" || !utmCampaign) {
    return undefined;
  }
  const utmContent = firstSearchParam(search.utm_content);
  const siteSourceName = metaSiteSource(search.site_source_name);
  return {
    utmSource,
    utmMedium,
    utmCampaign,
    ...(utmContent ? { utmContent } : {}),
    ...(siteSourceName ? { siteSourceName } : {}),
  };
}

/**
 * Persist Meta lead-gen UTMs from the landing URL so they survive email
 * verification and multi-step intake.
 */
export function captureBrokerLeadAttributionFromUrl() {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get("utm_source");
  const utmMedium = params.get("utm_medium");
  const utmCampaign = params.get("utm_campaign");

  if (utmSource !== "meta" || utmMedium !== "paid" || !utmCampaign) {
    memoryAttribution = null;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(VIEW_CONTENT_STORAGE_KEY);
    } catch {
      // Ignore private-mode storage failures.
    }
    return;
  }

  const payload: BrokerLeadAttribution = {
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent: params.get("utm_content") ?? undefined,
    siteSourceName: metaSiteSource(params.get("site_source_name") ?? undefined),
    fbclid: params.get("fbclid") ?? undefined,
    eventSourceUrl: window.location.href,
  };

  memoryAttribution = payload;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    trackStoredBrokerLeadViewContent();
  } catch {
    // Ignore quota / private-mode failures; submit will simply skip attribution.
  }
}

const VIEW_CONTENT_PARAMS = {
  content_category: "seller_intake",
  content_name: "Broker seller intake",
} as const;

/**
 * Fire ViewContent after the pixel script exists. A miss does not mark the
 * visit tracked, so a later load can retry.
 */
export function trackStoredBrokerLeadViewContent() {
  if (typeof window === "undefined") return;

  try {
    if (sessionStorage.getItem(VIEW_CONTENT_STORAGE_KEY)) return;
    if (!sessionStorage.getItem(STORAGE_KEY)) return;
    if (!metaPixel.event("ViewContent", { ...VIEW_CONTENT_PARAMS })) return;
    sessionStorage.setItem(VIEW_CONTENT_STORAGE_KEY, "1");
  } catch {
    // Ignore private-mode storage failures.
  }
}

export type BrokerLeadFormOpenRequest = {
  campaignId: string;
  creativeId?: string;
  dedupeKey: string;
};

/**
 * One open per Meta ad click. Visits without a click id (ad review, previews)
 * are not form opens. Returns nothing once this tab has already recorded.
 */
export function takeBrokerLeadFormOpenRequest():
  | BrokerLeadFormOpenRequest
  | undefined {
  if (typeof window === "undefined") return undefined;

  try {
    if (sessionStorage.getItem(FORM_OPEN_STORAGE_KEY)) return undefined;
    const stored = getStoredBrokerLeadAttribution();
    if (!stored) return undefined;

    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw
      ? (JSON.parse(raw) as Partial<BrokerLeadAttribution>)
      : null;
    const fbclid =
      parsed && typeof parsed.fbclid === "string" ? parsed.fbclid : undefined;
    if (!fbclid) return undefined;

    sessionStorage.setItem(FORM_OPEN_STORAGE_KEY, "1");
    return {
      campaignId: stored.utmCampaign,
      creativeId: stored.utmContent,
      dedupeKey: fbclid,
    };
  } catch {
    return undefined;
  }
}

export function releaseBrokerLeadFormOpenRequest() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(FORM_OPEN_STORAGE_KEY);
  } catch {
    // Ignore private-mode storage failures.
  }
}

export function getStoredBrokerLeadAttribution():
  | BrokerLeadAttributionEntry
  | undefined {
  if (typeof window === "undefined") return undefined;

  let stored: Partial<BrokerLeadAttribution> | null = memoryAttribution;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) stored = JSON.parse(raw) as Partial<BrokerLeadAttribution>;
  } catch {
    /* The current visit remains attributable without browser storage. */
  }
  if (
    typeof stored?.utmSource !== "string" ||
    typeof stored.utmMedium !== "string" ||
    typeof stored.utmCampaign !== "string"
  )
    return undefined;
  return {
    utmSource: stored.utmSource,
    utmMedium: stored.utmMedium,
    utmCampaign: stored.utmCampaign,
    utmContent:
      typeof stored.utmContent === "string" ? stored.utmContent : undefined,
    ...(stored.siteSourceName === "fb" || stored.siteSourceName === "ig"
      ? { siteSourceName: stored.siteSourceName }
      : {}),
  };
}

export function getBrokerLeadAttributionForSubmit():
  | (BrokerLeadAttribution & {
      fbc?: string;
      fbp?: string;
      metaEventId: string;
      eventSourceUrl: string;
    })
  | undefined {
  if (typeof window === "undefined") return undefined;

  let stored: BrokerLeadAttribution | null = memoryAttribution;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      stored = JSON.parse(raw) as BrokerLeadAttribution;
    }
  } catch {
    // The in-memory visit still supplies attribution in restricted browsers.
  }

  if (!stored?.utmCampaign) {
    return undefined;
  }

  let clickIds: { fbc?: string; fbp?: string } = {};
  try {
    clickIds = metaPixel.getMetaClickIds();
  } catch {
    /* Cookies may be blocked too. */
  }
  const { fbc: cookieFbc, fbp } = clickIds;
  const fbc =
    cookieFbc ?? (stored.fbclid ? buildMetaFbc(stored.fbclid) : undefined);
  const siteSourceName =
    stored.siteSourceName === "fb" || stored.siteSourceName === "ig"
      ? stored.siteSourceName
      : undefined;
  const storedWithoutSiteSource = { ...stored };
  delete storedWithoutSiteSource.siteSourceName;

  return {
    ...storedWithoutSiteSource,
    ...(siteSourceName ? { siteSourceName } : {}),
    fbc,
    fbp,
    metaEventId: crypto.randomUUID(),
    eventSourceUrl: stored.eventSourceUrl ?? window.location.href,
  };
}

export function trackBrokerLeadPixel(metaEventId: string) {
  try {
    metaPixel.event("Lead", {}, { eventID: metaEventId });
  } catch {
    /* Tracking cannot turn a saved intake into an error. */
  }
}
